import { useEffect, useMemo, useState } from 'react';
import { useSocket } from '@/lib/socket';
import { markNetSuiteUpdated, markNetSuiteRefreshing } from '@/components/operations/netsuiteFreshness';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationContext } from '@/contexts/LocationContext';
import { Stage } from '@/components/operations/types';
import { deriveWorkOrderStatus, OperationSummary, WorkOrderOutcome, WorkOrderRow } from './types';
import { buildWorkOrderConfig, RawWorkOrder } from './configMap';
import {
  ApiWorkOrderSnapshot,
  listWorkOrderHistory,
} from '@/lib/workOrderHistoryApi';
import { rowMatchesState, selectedStateCode } from '@/lib/locationFilter';

export type { RawWorkOrder };

const HISTORY_LOOKBACK_DAYS = 90;
const MS_PER_DAY = 86_400_000;
const HISTORY_LIMIT = 5000;

interface RawSalesOrder {
  id: string;
  orderNumber: string;
  date: string;
  customer: string;
  location: string;
  workOrders?: RawWorkOrder[];
}

const parseSlashDate = (raw: string) => {
  if (!raw) return 0;
  const [m, d, y] = raw.split('/').map(Number);
  if (!m || !y) return 0;
  return new Date(y, (m || 1) - 1, d || 1).getTime();
};

const parseIsoTs = (s?: string) => {
  if (!s) return 0;
  const n = Date.parse(s);
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (ts: number) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

function buildOperationsFromWO(wo: RawWorkOrder): OperationSummary[] {
  if (wo.isTKS0026) {
    return [
      { stage: 'Metal' as Stage, isComplete: wo.metalComplete },
      { stage: 'Assembly' as Stage, isComplete: wo.finalAssemblyComplete },
    ];
  }
  return [
    { stage: 'Kitting', isComplete: wo.kittingComplete },
    { stage: 'Fabric', isComplete: wo.fabricComplete },
    { stage: 'Metal', isComplete: wo.metalComplete },
    { stage: 'Assembly', isComplete: wo.finalAssemblyComplete },
  ];
}

function buildOperationsFromSnapshot(snap: ApiWorkOrderSnapshot): OperationSummary[] {
  if (snap.isTKS0026) {
    return [
      { stage: 'Metal' as Stage, isComplete: snap.metalComplete },
      { stage: 'Assembly' as Stage, isComplete: snap.finalAssemblyComplete },
    ];
  }
  return [
    { stage: 'Kitting', isComplete: snap.kittingComplete },
    { stage: 'Fabric', isComplete: snap.fabricComplete },
    { stage: 'Metal', isComplete: snap.metalComplete },
    { stage: 'Assembly', isComplete: snap.finalAssemblyComplete },
  ];
}

function liveRow(so: RawSalesOrder, wo: RawWorkOrder): WorkOrderRow {
  const ops = buildOperationsFromWO(wo);
  const opsTotal = ops.length;
  const opsComplete = ops.filter((o) => o.isComplete).length;
  const nextOp = ops.find((o) => !o.isComplete);
  const allComplete = opsComplete === opsTotal;
  const outcome: WorkOrderOutcome = 'Active';
  return {
    id: wo.id,
    workOrderId: wo.orderID,
    orderNumber: so.orderNumber,
    salesOrderId: so.id,
    customer: so.customer,
    fabricType: wo.fabricType || '',
    frameColor: wo.frameColor || '',
    isTKS0026: !!wo.isTKS0026,
    date: so.date,
    dateTs: parseSlashDate(so.date),
    location: so.location,
    operations: ops,
    opsTotal,
    opsComplete,
    opsPercent: opsTotal === 0 ? 0 : Math.round((opsComplete / opsTotal) * 100),
    nextStage: nextOp?.stage ?? '—',
    allComplete,
    kittingComplete: !!wo.kittingComplete,
    fabricComplete: !!wo.fabricComplete,
    metalComplete: !!wo.metalComplete,
    finalAssemblyComplete: !!wo.finalAssemblyComplete,
    outcome,
    status: deriveWorkOrderStatus(outcome, allComplete),
    builtAt: '',
    builtTs: 0,
    completedAt: '',
    completedTs: 0,
    config: buildWorkOrderConfig(wo),
  };
}

function historyRow(snap: ApiWorkOrderSnapshot): WorkOrderRow {
  const ops = buildOperationsFromSnapshot(snap);
  const opsTotal = ops.length;
  const opsComplete = ops.filter((o) => o.isComplete).length;
  const nextOp = ops.find((o) => !o.isComplete);
  const allComplete = opsTotal > 0 && opsComplete === opsTotal;
  const builtTs = parseIsoTs(snap.builtAt);
  const completedTs = parseIsoTs(snap.completedAt);
  let outcome: WorkOrderOutcome;
  if (snap.isBuilt) outcome = 'Built';
  else if (completedTs) outcome = 'Closed Unbuilt';
  else outcome = 'Active';
  return {
    id: snap.workOrderInternalId,
    workOrderId: snap.orderID,
    orderNumber: snap.orderNumber,
    salesOrderId: snap.salesOrderId,
    customer: snap.customer,
    fabricType: (snap.config as any)?.fabricType ?? '',
    frameColor: (snap.config as any)?.frameColor ?? '',
    isTKS0026: !!snap.isTKS0026,
    date: snap.date,
    dateTs: parseSlashDate(snap.date),
    location: snap.location,
    operations: ops,
    opsTotal,
    opsComplete,
    opsPercent: opsTotal === 0 ? 0 : Math.round((opsComplete / opsTotal) * 100),
    nextStage: nextOp?.stage ?? '—',
    allComplete,
    kittingComplete: !!snap.kittingComplete,
    fabricComplete: !!snap.fabricComplete,
    metalComplete: !!snap.metalComplete,
    finalAssemblyComplete: !!snap.finalAssemblyComplete,
    outcome,
    status: deriveWorkOrderStatus(outcome, allComplete),
    builtAt: fmtDate(builtTs),
    builtTs,
    completedAt: fmtDate(completedTs),
    completedTs,
    config: snap.config,
  };
}

export function useWorkOrdersData(): WorkOrderRow[] {
  const socket = useSocket();
  const { token } = useAuth();
  const { selectedLocation } = useLocationContext();
  const stateCode = selectedStateCode(selectedLocation);
  const [orders, setOrders] = useState<RawSalesOrder[]>([]);
  const [history, setHistory] = useState<ApiWorkOrderSnapshot[]>([]);

  useEffect(() => {
    if (!socket) return;
    const handleData = (data: RawSalesOrder[]) => {
      if (Array.isArray(data)) setOrders(data);
      markNetSuiteUpdated();
    };
    socket.on('netsuiteData', handleData);
    socket.on('netsuiteRefreshing', markNetSuiteRefreshing);
    socket.emit('requestNetSuiteData');
    return () => {
      socket.off('netsuiteData', handleData);
      socket.off('netsuiteRefreshing', markNetSuiteRefreshing);
    };
  }, [socket]);

  // History fetch — last N days, completed and not. Loaded once per mount;
  // refresh happens whenever the component remounts (e.g. tab switch).
  useEffect(() => {
    let cancelled = false;
    const from = new Date(Date.now() - HISTORY_LOOKBACK_DAYS * MS_PER_DAY).toISOString();
    listWorkOrderHistory(token, { completed: 'all', from, limit: HISTORY_LIMIT })
      .then((data) => {
        if (!cancelled) setHistory(data);
      })
      .catch((err) => {
        console.error('Failed to load WO history', err);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return useMemo(() => {
    const byId = new Map<string, WorkOrderRow>();
    // History first; live overrides.
    for (const snap of history) {
      byId.set(snap.workOrderInternalId, historyRow(snap));
    }
    for (const so of orders) {
      so.workOrders?.forEach((wo) => {
        byId.set(wo.id, liveRow(so, wo));
      });
    }
    const all = Array.from(byId.values());
    if (!stateCode) return all;
    return all.filter((r) => rowMatchesState(r.location, stateCode));
  }, [orders, history, stateCode]);
}
